export const propertyPaths = {
  '/properties': {
    get: {
      summary: 'List properties',
      operationId: 'listProperties',
      parameters: [
        {
          name: 'search',
          in: 'query',
          schema: {
            type: 'string',
          },
          description: 'Search term to filter properties by address, city, state, or zip code',
        },
        {
          name: 'association_id',
          in: 'query',
          schema: {
            type: 'integer',
          },
          description: 'Filter properties by association ID',
        },
        {
          name: 'limit',
          in: 'query',
          schema: {
            type: 'integer',
            default: 100,
          },
          description: 'Number of items to return',
        },
        {
          name: 'offset',
          in: 'query',
          schema: {
            type: 'integer',
            default: 0,
          },
          description: 'Number of items to skip',
        },
      ],
      responses: {
        '200': {
          description: 'A list of properties',
          content: {
            'application/json': {
              schema: {
                type: 'array',
                items: {
                  $ref: '#/components/schemas/Property',
                },
              },
            },
          },
        },
      },
    },
    post: {
      summary: 'Create a new property',
      operationId: 'createProperty',
      requestBody: {
        required: true,
        content: {
          'application/json': {
            schema: {
              $ref: '#/components/schemas/Property',
            },
          },
        },
      },
      responses: {
        '201': {
          description: 'The created property',
          content: {
            'application/json': {
              schema: {
                $ref: '#/components/schemas/Property',
              },
            },
          },
        },
      },
    },
  },
  '/properties/{id}': {
    get: {
      summary: 'Get a property by ID',
      operationId: 'getProperty',
      parameters: [
        {
          name: 'id',
          in: 'path',
          required: true,
          schema: {
            type: 'integer',
          },
        },
      ],
      responses: {
        '200': {
          description: 'The requested property',
          content: {
            'application/json': {
              schema: {
                $ref: '#/components/schemas/Property',
              },
            },
          },
        },
      },
    },
    put: {
      summary: 'Update a property',
      operationId: 'updateProperty',
      parameters: [
        {
          name: 'id',
          in: 'path',
          required: true,
          schema: {
            type: 'integer',
          },
        },
      ],
      requestBody: {
        required: true,
        content: {
          'application/json': {
            schema: {
              $ref: '#/components/schemas/Property',
            },
          },
        },
      },
      responses: {
        '200': {
          description: 'The updated property',
          content: {
            'application/json': {
              schema: {
                $ref: '#/components/schemas/Property',
              },
            },
          },
        },
      },
    },
    delete: {
      summary: 'Delete a property',
      operationId: 'deleteProperty',
      parameters: [
        {
          name: 'id',
          in: 'path',
          required: true,
          schema: {
            type: 'integer',
          },
        },
      ],
      responses: {
        '204': {
          description: 'Property deleted successfully',
        },
      },
    },
  },
  '/properties/autocomplete': {
    get: {
      summary: 'Autocomplete property search',
      operationId: 'autocompleteProperties',
      parameters: [
        {
          name: 'search',
          in: 'query',
          schema: {
            type: 'string',
          },
          description: 'Search term for property address',
          required: true,
        },
        {
          name: 'association_id',
          in: 'query',
          schema: {
            type: 'integer',
          },
          description: 'Filter by association ID',
        },
        {
          name: 'limit',
          in: 'query',
          schema: {
            type: 'integer',
            default: 10,
          },
          description: 'Number of results to return',
        },
      ],
      responses: {
        '200': {
          description: 'List of matching properties',
          content: {
            'application/json': {
              schema: {
                type: 'array',
                items: {
                  $ref: '#/components/schemas/Property',
                },
              },
            },
          },
        },
      },
    },
  },
  '/properties/import': {
    post: {
      summary: 'Import properties from CSV',
      operationId: 'importProperties',
      requestBody: {
        required: true,
        content: {
          'multipart/form-data': {
            schema: {
              type: 'object',
              properties: {
                file: {
                  type: 'string',
                  format: 'binary',
                  description: 'CSV file containing property data',
                },
              },
            },
          },
        },
      },
      responses: {
        '200': {
          description: 'Properties imported successfully',
          content: {
            'application/json': {
              schema: {
                type: 'object',
                properties: {
                  success: { type: 'boolean' },
                  message: { type: 'string' },
                  imported: { type: 'integer' },
                  errors: { 
                    type: 'array',
                    items: { type: 'string' }
                  },
                },
              },
            },
          },
        },
      },
    },
  },
  '/properties/import-with-association': {
    post: {
      summary: 'Import properties with association references from CSV',
      operationId: 'importPropertiesWithAssociation',
      requestBody: {
        required: true,
        content: {
          'multipart/form-data': {
            schema: {
              type: 'object',
              properties: {
                file: {
                  type: 'string',
                  format: 'binary',
                  description: 'CSV file containing property data with association external IDs',
                },
              },
            },
          },
        },
      },
      responses: {
        '200': {
          description: 'Properties imported successfully',
          content: {
            'application/json': {
              schema: {
                type: 'object',
                properties: {
                  success: { type: 'boolean' },
                  message: { type: 'string' },
                  imported: { type: 'integer' },
                  errors: { 
                    type: 'array',
                    items: { type: 'string' }
                  },
                },
              },
            },
          },
        },
      },
    },
  },
  '/properties/import-with-association-async': {
    post: {
      summary: 'Import properties with association references from CSV (Asynchronous)',
      operationId: 'importPropertiesWithAssociationAsync',
      requestBody: {
        required: true,
        content: {
          'multipart/form-data': {
            schema: {
              type: 'object',
              properties: {
                file: {
                  type: 'string',
                  format: 'binary',
                  description: 'CSV file containing property data with association external IDs',
                },
              },
            },
          },
        },
      },
      responses: {
        '202': {
          description: 'Import job started successfully',
          content: {
            'application/json': {
              schema: {
                type: 'object',
                properties: {
                  jobId: { type: 'string' },
                  message: { type: 'string' },
                },
              },
            },
          },
        },
      },
    },
  },
  '/properties/import-job-status/{jobId}': {
    get: {
      summary: 'Check the status of an import job',
      operationId: 'checkImportJobStatus',
      parameters: [
        {
          name: 'jobId',
          in: 'path',
          required: true,
          schema: {
            type: 'string',
          },
          description: 'ID of the import job',
        },
      ],
      responses: {
        '200': {
          description: 'Current status of the import job',
          content: {
            'application/json': {
              schema: {
                type: 'object',
                properties: {
                  jobId: { type: 'string' },
                  status: { 
                    type: 'string',
                    enum: ['pending', 'processing', 'completed', 'failed']
                  },
                  imported: { type: 'integer' },
                  total: { type: 'integer' },
                  errors: { 
                    type: 'array',
                    items: { type: 'string' }
                  },
                },
              },
            },
          },
        },
      },
    },
  },
  '/properties/export-csv': {
    get: {
      summary: 'Export properties to CSV',
      operationId: 'exportPropertiesCsv',
      parameters: [
        {
          name: 'association_id',
          in: 'query',
          schema: {
            type: 'integer',
          },
          description: 'Filter by association ID',
        },
      ],
      responses: {
        '200': {
          description: 'CSV file containing property data',
          content: {
            'text/csv': {
              schema: {
                type: 'string',
                format: 'binary',
              },
            },
          },
        },
      },
    },
  },
  '/properties/{id}/default': {
    post: {
      summary: 'Set property as default for its association',
      operationId: 'setDefaultProperty',
      parameters: [
        {
          name: 'id',
          in: 'path',
          required: true,
          schema: {
            type: 'integer',
          },
          description: 'ID of the property to set as default',
        },
      ],
      responses: {
        '200': {
          description: 'Property set as default successfully',
          content: {
            'application/json': {
              schema: {
                type: 'object',
                properties: {
                  success: { type: 'boolean' },
                  message: { type: 'string' },
                },
              },
            },
          },
        },
      },
    },
  },
};
